import React, { useEffect, useState, useRef } from "react";
import { api, fmtCurrency } from "../lib/api";
import { PageHeader } from "../components/layout/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, Trash, Calculator as CalcIcon, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Pricing() {
  return (
    <div>
      <PageHeader
        overline="Deal Math"
        title="Pricing / BoQ Engine"
        subtitle="Two calculators. One truth. Simple markup for quick quotes, detailed BoQ for tenders."
      />
      <Tabs defaultValue="simple">
        <TabsList data-testid="pricing-tabs" className="bg-white border border-slate-200">
          <TabsTrigger data-testid="tab-simple" value="simple">Simple Markup</TabsTrigger>
          <TabsTrigger data-testid="tab-boq" value="boq">Detailed BoQ</TabsTrigger>
        </TabsList>
        <TabsContent value="simple"><SimpleCalculator /></TabsContent>
        <TabsContent value="boq"><BoQCalculator /></TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleCalculator() {
  const [inp, setInp] = useState({ hardware_cost: 50000, hardware_markup_pct: 15, labor_days: 5, labor_day_rate: 800, field_tech_hours: 16, field_tech_rate: 55, margin_pct: 22 });
  const [result, setResult] = useState(null);

  const calc = async () => { try { setResult((await api.post("/pricing/simple", inp)).data); } catch { toast.error("Calculation failed"); } };
  useEffect(() => { calc(); }, []);

  const field = (k, label) => (
    <div>
      <Label className="text-xs text-slate-500 uppercase tracking-wider">{label}</Label>
      <Input data-testid={`simple-${k}`} type="number" value={inp[k]} onChange={(e) => setInp({ ...inp, [k]: parseFloat(e.target.value) || 0 })} className="bg-slate-50 border-slate-300 mt-1 font-mono" />
    </div>
  );

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="border border-slate-200 bg-white rounded-lg p-6">
        <div className="overline mb-4">Inputs</div>
        <div className="grid grid-cols-2 gap-4">
          {field("hardware_cost", "Hardware Cost")}
          {field("hardware_markup_pct", "Hardware Markup %")}
          {field("labor_days", "Labor Days")}
          {field("labor_day_rate", "Engineer Day Rate")}
          {field("field_tech_hours", "Field Tech Hours")}
          {field("field_tech_rate", "Field Tech Rate/hr")}
          <div className="col-span-2">{field("margin_pct", "Overall Margin %")}</div>
        </div>
        <Button data-testid="simple-calc-btn" onClick={calc} className="w-full mt-4 bg-[#FFC107] hover:bg-[#E5A906] font-mono uppercase text-xs tracking-wider">
          <CalcIcon size={14} className="mr-2" /> Recalculate
        </Button>
      </div>
      <div className="border border-slate-200 bg-white rounded-lg p-6">
        <div className="overline mb-4">Result</div>
        {result && (
          <div className="space-y-3">
            <Row label="Hardware (sell)" value={fmtCurrency(result.hardware_sell)} />
            <Row label="Engineer Labor" value={fmtCurrency(result.labor_cost)} />
            <Row label="Field Tech Cost" value={fmtCurrency(result.field_tech_cost)} />
            <div className="border-t border-slate-200 pt-3">
              <Row label="Subtotal (Cost)" value={fmtCurrency(result.subtotal_cost)} muted />
              <Row label="Subtotal (Sell)" value={fmtCurrency(result.subtotal_sell)} muted />
            </div>
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wider text-slate-500">Final Price</span>
                <span data-testid="simple-final-price" className="font-display font-bold text-3xl text-[#0F1E38] font-mono">{fmtCurrency(result.final_price)}</span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs text-emerald-400">Profit</span>
                <span className="font-mono text-emerald-400">{fmtCurrency(result.profit)} · {result.effective_margin_pct}%</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, muted }) => (
  <div className="flex justify-between items-baseline">
    <span className={`text-sm ${muted ? "text-slate-500" : "text-slate-700"}`}>{label}</span>
    <span className={`font-mono ${muted ? "text-slate-500 text-sm" : "text-[#0F1E38]"}`}>{value}</span>
  </div>
);

function BoQCalculator() {
  const [client, setClient] = useState("");
  const [quoteRef, setQuoteRef] = useState(`Q-${new Date().toISOString().slice(0, 10)}`);
  const [items, setItems] = useState([
    { description: "Cisco Catalyst 9300-48P", sku: "C9300-48P-A", quantity: 4, unit_price: 12500, discount_pct: 8, tax_pct: 18 },
    { description: "Palo Alto PA-3220 NGFW", sku: "PAN-PA-3220", quantity: 2, unit_price: 28000, discount_pct: 5, tax_pct: 18 },
  ]);
  const [globalDisc, setGlobalDisc] = useState(0);
  const [globalTax, setGlobalTax] = useState(0);
  const [result, setResult] = useState(null);
  const printRef = useRef(null);

  const calc = async () => {
    try { setResult((await api.post("/pricing/boq", { items, global_discount_pct: globalDisc, global_tax_pct: globalTax })).data); }
    catch { toast.error("BoQ calc failed"); }
  };
  useEffect(() => { calc(); }, []);

  const updateItem = (i, patch) => setItems(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems([...items, { description: "New Line", sku: "", quantity: 1, unit_price: 0, discount_pct: 0, tax_pct: 18 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const exportPDF = () => {
    if (!result) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Popup blocked — enable popups for this site"); return; }
    const rows = result.lines.map((l, i) => `
      <tr>
        <td style="padding:8px 10px;border-bottom:1px solid #eaeaea;">${i + 1}</td>
        <td style="padding:8px 10px;border-bottom:1px solid #eaeaea;">${escapeHtml(l.description)}<div style="color:#888;font-size:10px;font-family:monospace;margin-top:2px;">${escapeHtml(l.sku || "")}</div></td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">${l.quantity}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">$${l.unit_price.toFixed(2)}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">${l.discount ? "-$" + l.discount.toFixed(2) : "—"}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;">$${l.tax.toFixed(2)}</td>
        <td style="text-align:right;padding:8px 10px;border-bottom:1px solid #eaeaea;font-family:monospace;font-weight:600;">$${l.total.toFixed(2)}</td>
      </tr>`).join("");

    const html = `<!doctype html><html><head><title>${escapeHtml(quoteRef)}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:40px;max-width:900px;margin:auto;}
        h1{font-size:28px;margin:0;letter-spacing:-0.02em;}
        .meta{color:#666;font-size:12px;margin-top:6px;}
        table{width:100%;border-collapse:collapse;margin-top:24px;font-size:12px;}
        th{text-align:left;padding:8px 10px;background:#f4f4f5;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:#555;}
        .totals{margin-top:20px;margin-left:auto;width:340px;font-size:13px;}
        .totals div{display:flex;justify-content:space-between;padding:6px 0;}
        .grand{border-top:2px solid #111;margin-top:6px;padding-top:10px;font-weight:700;font-size:18px;}
        .brand{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:16px;}
        .brand-r{text-align:right;font-size:11px;color:#666;}
        @media print { body { padding: 20px; } }
      </style></head><body>
      <div class="brand">
        <div>
          <h1>SoverenX · Bill of Quantity</h1>
          <div class="meta">Quote Ref: <strong>${escapeHtml(quoteRef)}</strong><br/>Client: <strong>${escapeHtml(client || "—")}</strong><br/>Date: ${new Date().toLocaleDateString()}</div>
        </div>
        <div class="brand-r">Network Integration Services<br/>Cisco · Fortinet · Palo Alto · F5 · Checkpoint</div>
      </div>
      <table>
        <thead><tr><th>#</th><th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Unit</th><th style="text-align:right;">Discount</th><th style="text-align:right;">Tax</th><th style="text-align:right;">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="totals">
        <div><span>Subtotal (net)</span><span>$${result.subtotal.toFixed(2)}</span></div>
        <div><span>Global Discount</span><span>- $${result.global_discount.toFixed(2)}</span></div>
        <div><span>Global Tax</span><span>+ $${result.global_tax.toFixed(2)}</span></div>
        <div class="grand"><span>Grand Total</span><span>$${result.grand_total.toFixed(2)}</span></div>
      </div>
      <div style="margin-top:60px;color:#888;font-size:11px;">Terms: Payment 30 days net. Prices valid 30 days from issue date.</div>
      <script>window.onload = () => { setTimeout(() => window.print(), 300); }<\/script>
      </body></html>`;
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 border border-slate-200 bg-white rounded-lg p-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Quote Ref</Label><Input value={quoteRef} onChange={(e) => setQuoteRef(e.target.value)} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Client</Label><Input data-testid="boq-client" value={client} onChange={(e) => setClient(e.target.value)} className="bg-slate-50 border-slate-300 mt-1"/></div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="overline">Bill of Quantity</div>
          <Button data-testid="boq-add-line-btn" size="sm" onClick={addItem} className="bg-[#FFC107] hover:bg-[#E5A906] h-8 text-xs font-mono uppercase"><Plus size={12} className="mr-1"/> Add Line</Button>
        </div>
        <div className="overflow-x-auto" ref={printRef}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left overline border-b border-slate-200">
                <th className="pb-2">Description</th><th>SKU</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc%</th><th className="text-right">Tax%</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} data-testid={`boq-row-${i}`} className="border-b border-slate-100">
                  <td className="py-2"><Input value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} className="bg-transparent border-slate-300 text-xs h-8"/></td>
                  <td><Input value={it.sku} onChange={(e) => updateItem(i, { sku: e.target.value })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-28"/></td>
                  <td className="text-right"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-16 text-right"/></td>
                  <td className="text-right"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-24 text-right"/></td>
                  <td className="text-right"><Input type="number" value={it.discount_pct} onChange={(e) => updateItem(i, { discount_pct: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-16 text-right"/></td>
                  <td className="text-right"><Input type="number" value={it.tax_pct} onChange={(e) => updateItem(i, { tax_pct: parseFloat(e.target.value) || 0 })} className="bg-transparent border-slate-300 text-xs h-8 font-mono w-16 text-right"/></td>
                  <td><button data-testid={`boq-remove-${i}`} onClick={() => removeItem(i)} className="text-slate-500 hover:text-red-400 ml-2" style={{transition:"color 0.15s ease"}}><Trash size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Global Discount %</Label><Input data-testid="boq-global-disc" type="number" value={globalDisc} onChange={(e) => setGlobalDisc(parseFloat(e.target.value) || 0)} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
          <div><Label className="text-xs text-slate-500 uppercase tracking-wider">Global Tax %</Label><Input data-testid="boq-global-tax" type="number" value={globalTax} onChange={(e) => setGlobalTax(parseFloat(e.target.value) || 0)} className="bg-slate-50 border-slate-300 mt-1 font-mono"/></div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button data-testid="boq-calc-btn" onClick={calc} className="bg-[#FFC107] hover:bg-[#E5A906] font-mono uppercase text-xs tracking-wider"><CalcIcon size={14} className="mr-2"/> Recalculate</Button>
          <Button data-testid="boq-pdf-btn" onClick={exportPDF} variant="outline" className="border-slate-300 bg-transparent hover:bg-slate-100 text-[#0F1E38] font-mono uppercase text-xs tracking-wider"><FilePdf size={14} className="mr-2"/> Export PDF</Button>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-lg p-6 h-fit sticky top-6">
        <div className="overline mb-4">Summary</div>
        {result && (
          <div className="space-y-3">
            <Row label="Subtotal (net)" value={fmtCurrency(result.subtotal)} muted />
            <Row label="Global Discount" value={`- ${fmtCurrency(result.global_discount)}`} muted />
            <Row label="Global Tax" value={`+ ${fmtCurrency(result.global_tax)}`} muted />
            <div className="border-t border-slate-200 pt-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-wider text-slate-500">Grand Total</span>
                <span data-testid="boq-grand-total" className="font-display font-bold text-2xl text-[#0F1E38] font-mono">{fmtCurrency(result.grand_total)}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}
